import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useState } from 'react';
const PARAMETER_TYPES = ['string', 'number', 'boolean', 'json', 'date'];
const defaultParameter = {
    cle: '',
    nom_param: '',
    valeur: '',
    type_param: 'string',
    description: '',
};
const normalizeDateValue = (value) => {
    if (!value) {
        return '';
    }
    if (value.length === 10) {
        return value;
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return '';
    }
    return date.toISOString().slice(0, 10);
};
const ParameterForm = ({ initialData, onSubmit, onCancel }) => {
    const startingValue = useMemo(() => {
        if (!initialData) {
            return { ...defaultParameter };
        }
        return {
            cle: initialData.cle,
            nom_param: initialData.nom_param,
            valeur: initialData.type_param === 'date'
                ? normalizeDateValue(initialData.valeur)
                : initialData.valeur,
            type_param: initialData.type_param,
            description: initialData.description ?? '',
        };
    }, [initialData]);
    const [formData, setFormData] = useState(startingValue);
    const [formError, setFormError] = useState(null);
    const handleChange = (event) => {
        const { name, value } = event.target;
        setFormData((previous) => ({ ...previous, [name]: value }));
    };
    const handleTypeChange = (event) => {
        const nextType = event.target.value;
        setFormData((previous) => ({
            ...previous,
            type_param: nextType,
            valeur: nextType === 'date' ? '' : previous.valeur,
        }));
    };
    const handleSubmit = (event) => {
        event.preventDefault();
        setFormError(null);
        if (!formData.cle.trim()) {
            setFormError('La clé est obligatoire.');
            return;
        }
        if (!formData.nom_param.trim()) {
            setFormError('Le nom est obligatoire.');
            return;
        }
        if (!formData.valeur.trim()) {
            setFormError('La valeur est obligatoire.');
            return;
        }
        let payload = { ...formData };
        if (formData.type_param === 'date') {
            const isoValue = new Date(`${formData.valeur}T00:00:00`).toISOString();
            payload = {
                ...payload,
                valeur: isoValue,
            };
        }
        if (payload.description !== undefined && payload.description !== null && payload.description.trim() === '') {
            payload = {
                ...payload,
                description: null,
            };
        }
        onSubmit(payload);
    };
    return (_jsxs("div", { className: "w-full bg-white", children: [_jsx("div", { className: "border-b bg-gray-100 p-4", children: _jsx("h2", { className: "text-lg font-semibold text-gray-800", children: initialData ? `Modifier ${initialData.cle}` : 'Nouveau paramètre' }) }), _jsxs("form", { className: "flex flex-col gap-4 p-4", onSubmit: handleSubmit, children: [_jsxs("div", { children: [_jsx("label", { htmlFor: "field-cle", className: "block text-sm font-medium text-gray-700", children: "Cl\u00E9" }), _jsx("input", { id: "field-cle", name: "cle", type: "text", value: formData.cle, onChange: handleChange, className: "mt-1 w-full rounded border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none", placeholder: "app.parametre" })] }), _jsxs("div", { children: [_jsx("label", { htmlFor: "field-nom", className: "block text-sm font-medium text-gray-700", children: "Nom affich\u00E9" }), _jsx("input", { id: "field-nom", name: "nom_param", type: "text", value: formData.nom_param, onChange: handleChange, className: "mt-1 w-full rounded border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none", placeholder: "Intitul\u00E9 lisible" })] }), _jsxs("div", { children: [_jsx("label", { htmlFor: "field-type", className: "block text-sm font-medium text-gray-700", children: "Type" }), _jsx("select", { id: "field-type", name: "type_param", value: formData.type_param, onChange: handleTypeChange, className: "mt-1 w-full rounded border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none", children: PARAMETER_TYPES.map((type) => (_jsx("option", { value: type, children: type }, type))) })] }), _jsxs("div", { children: [_jsx("label", { htmlFor: "field-valeur", className: "block text-sm font-medium text-gray-700", children: "Valeur" }), formData.type_param === 'boolean' ? (_jsxs("select", { id: "field-valeur", name: "valeur", value: formData.valeur, onChange: handleChange, className: "mt-1 w-full rounded border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none", children: [_jsx("option", { value: "true", children: "true" }), _jsx("option", { value: "false", children: "false" })] })) : formData.type_param === 'json' ? (_jsx("textarea", { id: "field-valeur", name: "valeur", value: formData.valeur, onChange: handleChange, rows: 4, className: "mt-1 w-full rounded border border-gray-300 px-3 py-2 font-mono text-sm focus:border-blue-500 focus:outline-none", placeholder: '{ "cl\u00E9": "valeur" }' })) : (_jsx("input", { id: "field-valeur", name: "valeur", type: formData.type_param === 'date' ? 'date' : 'text', value: formData.valeur, onChange: handleChange, className: "mt-1 w-full rounded border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none", placeholder: "Valeur" }))] }), _jsxs("div", { children: [_jsx("label", { htmlFor: "field-description", className: "block text-sm font-medium text-gray-700", children: "Description" }), _jsx("textarea", { id: "field-description", name: "description", value: formData.description ?? '', onChange: handleChange, rows: 3, className: "mt-1 w-full rounded border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none", placeholder: "Informations compl\u00E9mentaires" })] }), formError && _jsx("p", { className: "text-sm text-red-600", children: formError }), _jsxs("div", { className: "mt-2 flex flex-col justify-end gap-3 sm:flex-row", children: [_jsx("button", { type: "button", onClick: onCancel, className: "rounded bg-gray-200 px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-300", children: "Annuler" }), _jsx("button", { type: "submit", className: "rounded bg-blue-500 px-4 py-2 text-sm font-medium text-white transition hover:bg-blue-600", children: "Enregistrer" })] })] })] }));
};
export default ParameterForm;
